// Bonworld Insights — editorial content
// Each essay has a slug, category, title, dek, publish date, and body sections.

export const insights = [
  {
    slug: "the-patience-premium",
    category: "Investment Philosophy",
    title: "The Patience Premium",
    dek: "On time as the last remaining edge in a market that has priced away everything else.",
    date: "2026-01-20",
    reading: "6 min read",
    author: "Thomas Nagelsmann",
    sections: [
      {
        heading: "The market prices information. It does not price patience.",
        body:
          "Every ounce of proprietary insight and every millisecond of latency has, over the past two decades, been ground out of the returns of listed markets. What has not been priced away is the willingness to sit — through drawdowns, through boredom, through the temptation of the next thing. This is Bonworld's advantage, and it is one of the few advantages left."
      },
      {
        heading: "Compounding is a function of restraint.",
        body:
          "A family that cannot stay invested in a compounder for twenty years cannot enjoy the compounder. The office's real work, then, is not the selection of assets. It is the design of the human system around those assets — governance, liquidity buffers, communication rhythms — that permits the family to remain."
      },
      {
        heading: "Three practical implications.",
        body:
          "First, the reserve pocket must be genuinely reserve-grade, not merely 'lower risk'. Second, illiquidity should be sized to what the family can defend emotionally, not merely mathematically. Third, the story the family tells itself about the portfolio must be simple enough to repeat in a bad year."
      }
    ]
  },
  {
    slug: "on-being-owners",
    category: "Portfolio Construction",
    title: "On Being Owners, Not Renters",
    dek: "Why direct co-investment remains the highest form of ownership — and the most demanding.",
    date: "2025-11-14",
    reading: "5 min read",
    author: "Alexander Padel",
    sections: [
      {
        heading: "Renting looks like ownership. It is not.",
        body:
          "A fund holds an asset for six or seven years, then must exit. A family holds an asset for a generation, then hands it to the next. These are not the same thing. Our preference — wherever the capability exists — is to co-invest as principals, and to hold."
      },
      {
        heading: "The cost of ownership.",
        body:
          "Direct ownership means governance. It means board seats, difficult conversations, capital calls, and the responsibility of a name on a document. A well-built family office is the infrastructure that lets a family bear that cost without being consumed by it."
      },
      {
        heading: "What we underwrite for.",
        body:
          "Cashflow durability. Reinvestment runway. Alignment of the operating team over cycles. Governance clean enough to survive succession. Returns are the by-product of these — not the input."
      }
    ]
  },
  {
    slug: "the-family-constitution",
    category: "Family Governance",
    title: "The Family Constitution: A Working Draft",
    dek: "A short list of the questions every principal family should have answered — in writing — before the next generation votes.",
    date: "2025-09-02",
    reading: "7 min read",
    author: "Philippe Siegman",
    sections: [
      {
        heading: "The constitution is not a legal instrument.",
        body:
          "It is a compact between family members about how decisions will be made, how disagreements will be resolved, and what the family stands for. Trust deeds, holding company constitutions and shareholder agreements are its scaffolding, not its content."
      },
      {
        heading: "Six questions to answer.",
        body:
          "Who speaks for the family externally? Who breaks a tie internally? How does a family member enter the business, and how do they exit? How is philanthropy chosen and measured? What is off the table for reasons of values, not returns? And — most importantly — how will this document itself be amended?"
      },
      {
        heading: "Write it before you need it.",
        body:
          "The best time to draft a constitution is when nothing is on fire. The second-best time is now."
      }
    ]
  },
  {
    slug: "singapore-and-the-long-view",
    category: "Perspective",
    title: "Singapore and the Long View",
    dek: "Why the Republic remains, quietly, one of the few remaining jurisdictions built for multi-generational capital.",
    date: "2025-06-18",
    reading: "5 min read",
    author: "Jungang Chen",
    sections: [
      {
        heading: "Institutions before markets.",
        body:
          "Singapore's advantage is not tax; it is the compound interest of institutions. The rule of law, a competent regulator, a stable currency, and a public administration that plans in decades — this is the substrate on which family capital can rest."
      },
      {
        heading: "Neutrality in a fragmenting world.",
        body:
          "As the global order pulls apart, families are searching for jurisdictions that can conduct themselves in more than one language and one alliance. Singapore, in that sense, is not a hedge but a home."
      },
      {
        heading: "A quiet address.",
        body:
          "From the fifteenth floor of Peninsula Plaza, we watch a city that has understood something rare: that discretion, and the institutions that protect it, are themselves a form of wealth."
      }
    ]
  },
  {
    slug: "the-quiet-decade-ahead",
    category: "Macro & Markets",
    title: "The Quiet Decade Ahead",
    dek: "Portfolio implications of a world in which real rates are positive, capital is scarce again, and quality reasserts itself.",
    date: "2025-03-25",
    reading: "6 min read",
    author: "Dr. Peter Bonhoeffer",
    sections: [
      {
        heading: "Cheap money was not neutral.",
        body:
          "A decade of near-zero rates flattered indices, subsidised mediocrity, and taught a generation that duration was free. It was not. The next decade will look more like the pre-2008 world than the post-2008 world: capital priced, discipline rewarded."
      },
      {
        heading: "Where we expect to be paid.",
        body:
          "Owners of durable, cash-generating businesses. Providers of capital where banks have withdrawn. Landlords of real assets whose replacement cost has re-rated. Managers who have compounded through three cycles without a blow-up."
      },
      {
        heading: "Where we expect to lose.",
        body:
          "Anywhere the return depends on multiple expansion. Anywhere the story is louder than the cashflow. Anywhere leverage substitutes for judgement."
      }
    ]
  },
  {
    slug: "letters-to-a-young-heir",
    category: "Next Generation",
    title: "Letters to a Young Heir",
    dek: "Notes we would send to a rising family member on the eve of their first board meeting.",
    date: "2024-12-01",
    reading: "8 min read",
    author: "Mei Ling Tan",
    sections: [
      {
        heading: "You are not the money. You are its custodian.",
        body:
          "The wealth existed before you and — done well — will exist after. Your task is not to make it larger. Your task is to hand it forward without breaking it, and to lead the human system around it into a decade you will not fully see."
      },
      {
        heading: "Learn to say no in six different ways.",
        body:
          "The most useful skill of the coming decade will not be picking winners. It will be politely declining the many propositions that will find you — and doing so in a way that leaves the relationship intact and the reputation clean."
      },
      {
        heading: "Speak last, decide slowly, act with conviction.",
        body:
          "You are watched. Everything you say in the boardroom will be repeated. Adopt the tempo of the office: listen for the meeting behind the meeting, sleep on decisions the family will inherit, and when you act — act cleanly, once, and for the record."
      }
    ]
  }
];

export const getInsight = (slug) => insights.find((x) => x.slug === slug);
